import { isNumber } from "lodash";
import { useMemo, useState } from "react";
import { CellProps, Column } from "react-table";

import {
  FindSyncHistoryQuery,
  SyncJobStatus,
  useFindSyncHistoryQuery,
} from "@/apollo/types";
import { BadgeVariant } from "@/components/elements/Badge_Legacy";
import { SecondaryButton } from "@/components/elements/Button";
import { Empty } from "@/components/elements/DataBox";
import DefaultTable from "@/components/elements/DefaultTable";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import { FormatTime } from "@/components/elements/FormatTime";
import Tooltip from "@/components/elements/Tooltip";
import cn from "@/helpers/classNames";
import useLoadingManager from "@/hooks/useLoadingManager";
import { formatRowCount } from "@/shared/formatters";
import {
  CalendarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

import { StatusLED } from "../elements/StatusLED";
import { ReverseEltJobLoggingModalContainer } from "./ReverseEltJobLogsModal";

type Row = FindSyncHistoryQuery["findReverseEltSyncHistory"][0];

function getStatusText(status: string, hasFailures: boolean) {
  switch (status) {
    case SyncJobStatus.Completed:
      if (hasFailures) {
        return "Completed with Errors";
      }
      return "Completed";
    case SyncJobStatus.Scheduled:
      return "Scheduled";
    case SyncJobStatus.Running:
      return "Running";
    case SyncJobStatus.Failed:
      return "Failed";
  }
  return "-";
}

function StatusIcon(props: { status: string; hasFailures: boolean }) {
  switch (props.status) {
    case SyncJobStatus.Completed:
      if (props.hasFailures) {
        return (
          <ExclamationTriangleIcon className="h-5 w-5 fill-current text-yellow-400" />
        );
      }
      return (
        <CheckCircleIcon className="h-5 w-5 fill-current text-green-500" />
      );
    case SyncJobStatus.Failed:
      return (
        <ExclamationCircleIcon className="h-5 w-5 fill-current text-red-500" />
      );
    case SyncJobStatus.Running:
      return (
        <div className="flex h-5 w-5 items-center justify-center">
          <StatusLED pulse variant={BadgeVariant.Default} />
        </div>
      );
    case SyncJobStatus.Scheduled:
      return <CalendarIcon className="h-5 w-5 text-muted-foreground" />;
  }
  return <div />;
}

function SyncStatus(props: { status: string; hasFailures: boolean }) {
  return (
    <div className="flex items-center space-x-2">
      <div className="shrink-0">
        <StatusIcon status={props.status} hasFailures={props.hasFailures} />
      </div>
      <div
        className={cn("text-sm font-medium leading-tight", {
          "font-normal text-muted-foreground":
            props.status === SyncJobStatus.Scheduled,
        })}
      >
        {getStatusText(props.status, props.hasFailures)}
      </div>
    </div>
  );
}

function RowCountHeader(props: {
  title: string;
  tooltip: string | JSX.Element;
}) {
  return (
    <Tooltip content={props.tooltip}>
      <div className={"flex items-center"}>
        {props.title}
        <InformationCircleIcon
          className={"ml-1 mr-2 h-4 w-4 text-muted-foreground"}
        />
      </div>
    </Tooltip>
  );
}

export function SyncHistory(props: { syncId: string }) {
  const { loading, data: { findReverseEltSyncHistory = [] } = {} } =
    useFindSyncHistoryQuery({
      variables: { syncId: props.syncId },
      skip: !props.syncId,
      pollInterval: 20_000,
      skipPollAttempt: () => document.hidden,
    });

  const columns: Column<Row>[] = useMemo(() => {
    return [
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }: CellProps<Row, Row["status"]>) => {
          const hasFailures =
            (row.original.newRecords ?? 0) > (row.original.syncedRecords ?? 0);
          return (
            <SyncStatus
              status={row.original.status ?? ""}
              hasFailures={hasFailures}
            />
          );
        },
      },
      {
        Header: (
          <RowCountHeader
            title="Total Rows"
            tooltip="The total number of rows retrieved from the source model during the sync job."
          />
        ),
        accessor: "totalRecords",
        Cell: ({ row, value }: CellProps<Row, Row["totalRecords"]>) => {
          if (
            row.original.status === SyncJobStatus.Scheduled ||
            !isNumber(value)
          ) {
            return <div className="text-sm text-muted-foreground">-</div>;
          }
          return <>{formatRowCount(value)}</>;
        },
      },
      {
        Header: (
          <RowCountHeader
            title="New Rows"
            tooltip={
              <>
                The number of rows that are new or have changed since the last
                sync.
                <br />
                This is the number of rows submitted to the destination.
                <br />
                It is 0, if no changes were detected in the source model since
                the last sync.
              </>
            }
          />
        ),
        accessor: "newRecords",
        Cell: ({ row, value }: CellProps<Row, Row["newRecords"]>) => {
          if (
            row.original.status === SyncJobStatus.Scheduled ||
            !isNumber(value)
          ) {
            return <div className="text-sm text-muted-foreground">-</div>;
          }
          return <>{formatRowCount(value)}</>;
        },
      },
      {
        Header: (
          <RowCountHeader
            title="Synced Rows"
            tooltip="The number of rows successfully synced to the destination."
          />
        ),
        accessor: "syncedRecords",
        Cell: ({ row, value }: CellProps<Row, Row["syncedRecords"]>) => {
          if (
            row.original.status === SyncJobStatus.Scheduled ||
            !isNumber(value)
          ) {
            return <div className="text-sm text-muted-foreground">-</div>;
          }
          return <>{formatRowCount(value)}</>;
        },
      },
      {
        Header: (
          <RowCountHeader
            title="Failed Rows"
            tooltip="The number of rows that failed to sync to the destination."
          />
        ),
        accessor: "failedRecords",
        Cell: ({ row, value }: CellProps<Row, Row["failedRecords"]>) => {
          if (
            row.original.status === SyncJobStatus.Scheduled ||
            !isNumber(value)
          ) {
            return <div className="text-sm text-muted-foreground">-</div>;
          }
          return <>{formatRowCount(value)}</>;
        },
      },
      {
        Header: "Started",
        accessor: "startedAt",
        Cell: ({ row, value }: CellProps<Row>) => {
          if (row.original.status === SyncJobStatus.Scheduled) {
            return <div className="text-sm text-muted-foreground">-</div>;
          }
          return (
            <Tooltip content={<FormatTime date={value} format="LLL" />}>
              <div className="whitespace-nowrap text-sm text-muted-foreground">
                <FormatRelativeTime date={value} />
              </div>
            </Tooltip>
          );
        },
      },
      {
        Header: "Logs",
        maxWidth: 100,
        Cell: ({ row }: CellProps<Row>) => {
          if (row.original.status === SyncJobStatus.Scheduled) {
            return <div className="h-6" />;
          }
          return (
            <SecondaryButton
              onClick={() => setClickedJob(row.original)}
              size="sm"
              className="h-6 px-2"
            >
              Logs
            </SecondaryButton>
          );
        },
      },
    ];
  }, []);

  const [clickedJob, setClickedJob] = useState<
    (typeof findReverseEltSyncHistory)[0] | null
  >(null);

  const EmptyHistory = () => {
    return <Empty>Your sync history will appear here.</Empty>;
  };

  return useLoadingManager({ loading, message: "Loading history" }, () => {
    if (!findReverseEltSyncHistory.length) return <EmptyHistory />;
    return (
      <div className="space-y-5">
        <DefaultTable
          isLoading={loading}
          columns={columns}
          data={findReverseEltSyncHistory}
          isSortable={false}
          paginate
        />

        {clickedJob && (
          <ReverseEltJobLoggingModalContainer
            parentJob={clickedJob}
            onClose={() => {
              setClickedJob(null);
            }}
          />
        )}
      </div>
    );
  });
}
